import { getOfflineDb, type QueuedCapture, type QueuedCaptureStatus } from './db';
import type {
  CaptureMode,
  CaptureWorkflow,
  QualityGateResult,
} from '../api/types';
import { createCapture, uploadCaptureAsset } from '../api/capture';
import { ApiError } from '../api/errors';

/** After this many consecutive failed attempts a queued capture stops
 * auto-retrying and moves to 'failed', where it shows up in the
 * flagged-capture list for the operator to retry by hand or discard. A
 * capture taken with no signal for hours must not burn the battery on an
 * unbounded retry loop, but it must also never silently vanish. */
const MAX_AUTO_RETRY_ATTEMPTS = 8;

/** What a row queued before Capture Jobs existed says on the queue and
 *  flagged screens. The API refuses a capture with no job, so retrying it
 *  can only burn the retry budget on a 400. */
export const NO_JOB_ERROR =
  'Taken before capture jobs, so it has no job and cannot be sent. Discard it and capture the item again from its job.';

export interface EnqueueAsset {
  fingerprint: string;
  contentType: string;
  bytes: Blob;
}

export interface EnqueueCaptureInput {
  mode: CaptureMode;
  workflow?: CaptureWorkflow;
  /** The capture's own key, per `captureFingerprint`. */
  fingerprint: string;
  /** In capture order, at least one and at most what the API accepts (3). */
  assets: EnqueueAsset[];
  /** The Capture Job the capture belongs to. Required: there is no capture
   *  without a job (CAPTURE_JOBS.md section 6). */
  captureJobId: string;
  sessionId: string;
  workItemId?: string;
  siteId?: string;
  teamId?: string;
  voiceTranscript?: string;
  clientQuality?: QualityGateResult;
  manualFields?: Record<string, string>;
}

/** Writes a capture to IndexedDB as 'pending' and returns immediately.
 * Called with no network required: this is what makes capture work
 * offline in the first place. */
export async function enqueueCapture(input: EnqueueCaptureInput): Promise<QueuedCapture> {
  const db = await getOfflineDb();
  const now = new Date().toISOString();
  const { assets, ...rest } = input;
  const record: QueuedCapture = {
    localId: crypto.randomUUID(),
    status: 'pending',
    attempts: 0,
    createdAt: now,
    updatedAt: now,
    ...rest,
    // Read the bytes out of each Blob before they reach IndexedDB, so the
    // stored record holds values every runtime can clone back.
    assets: await Promise.all(
      assets.map(async (asset) => ({
        fingerprint: asset.fingerprint,
        contentType: asset.contentType,
        bytes: await asset.bytes.arrayBuffer(),
      }))
    ),
  };
  await db.put('queue', record);
  return record;
}

/** Where a capture got to on the way out of the device. */
export type DeliveryState = 'uploaded' | 'waiting' | 'refused';

export interface DeliveryOutcome {
  state: DeliveryState;
  /**
   * The `queue` row's own key, present either way. A voice note recorded
   * against this capture before it has a server id attaches to the row by
   * this id (`voice-queue.ts` `queuedCaptureLocalId`), and resolves to
   * `captureId` once the row uploads.
   */
  localId: string;
  /**
   * The server's id for the capture, set once it has arrived. It is what
   * the capture screen needs to offer the operator the values that were
   * read, while they are still standing at the item.
   */
  captureId?: string;
  /** The API's own wording, set when `state` is 'refused' (HTTP 402). */
  message?: string;
}

/**
 * Enqueues the capture and, on a connection, drains the queue straight
 * away.
 *
 * `registerOfflineSync` drains on the `online` event and on app start, so a
 * capture taken on a working connection uploaded only once the operator
 * navigated to a screen that mounts the queue hook. Nothing was lost, but
 * the confirmation on the capture screen could claim nothing about where
 * the capture had got to, and the operator standing at the item had no way
 * to know it had reached extraction. The proof of concept analysed the
 * frame the moment it was taken; this is that behaviour on a queue that
 * still survives a dead connection.
 *
 * `processQueue` flips each item to 'uploading' before it calls out and
 * picks up only 'pending' items, so a drain running here beside one from
 * the queue screen does not upload the same capture twice.
 */
export async function enqueueAndDeliver(input: EnqueueCaptureInput): Promise<DeliveryOutcome> {
  const record = await enqueueCapture(input);
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return { state: 'waiting', localId: record.localId };
  }
  const result = await processQueue();
  if (!result.uploaded.includes(record.localId)) {
    // A 402 is an answer, not a dead connection: say what the API said.
    const db = await getOfflineDb();
    const held = await db.get('queue', record.localId);
    if (held?.refusal) {
      return { state: 'refused', localId: record.localId, message: held.lastError };
    }
    return { state: 'waiting', localId: record.localId };
  }
  // `processQueue` wrote the server's id onto the record it uploaded, so
  // the id comes back off the queue rather than being threaded out of the
  // drain, which also uploads whatever else was waiting.
  const db = await getOfflineDb();
  const delivered = await db.get('queue', record.localId);
  return {
    state: 'uploaded',
    localId: record.localId,
    ...(delivered?.captureId ? { captureId: delivered.captureId } : {}),
  };
}

export async function listQueue(): Promise<QueuedCapture[]> {
  const db = await getOfflineDb();
  return db.getAll('queue');
}

/**
 * The queued record a capture was sent from, once `POST /capture` has
 * answered with its id. The bytes are still in it, which is how a screen
 * shows the operator the frames they just shot without asking the server
 * for them and without a connection.
 *
 * The queue holds a handful of rows, so this reads them rather than
 * carrying a second index for a lookup that runs once per screen.
 */
export async function findQueuedCapture(captureId: string): Promise<QueuedCapture | undefined> {
  const db = await getOfflineDb();
  const rows = await db.getAll('queue');
  return rows.find((row) => row.captureId === captureId);
}

export async function listByStatus(status: QueuedCaptureStatus): Promise<QueuedCapture[]> {
  const db = await getOfflineDb();
  return db.getAllFromIndex('queue', 'by-status', status);
}

/** The flagged-capture list (BUILD_PLAN SC3 DELIVER): captures that
 * exhausted their auto-retry budget and need an operator decision. */
export function listFlagged(): Promise<QueuedCapture[]> {
  return listByStatus('failed');
}

export async function retryQueuedCapture(localId: string): Promise<void> {
  const db = await getOfflineDb();
  const record = await db.get('queue', localId);
  if (!record) return;
  record.status = 'pending';
  record.attempts = 0;
  record.lastError = undefined;
  record.refusal = undefined;
  record.updatedAt = new Date().toISOString();
  await db.put('queue', record);
}

export async function removeQueuedCapture(localId: string): Promise<void> {
  const db = await getOfflineDb();
  await db.delete('queue', localId);
}

interface CaptureUploader {
  uploadCaptureAsset: typeof uploadCaptureAsset;
  createCapture: typeof createCapture;
}

const defaultUploader: CaptureUploader = {
  uploadCaptureAsset,
  createCapture,
};

export interface ProcessQueueResult {
  uploaded: string[];
  failed: string[];
  stillPending: string[];
}

/**
 * Drains every 'pending' item in the queue against the server. Called on
 * reconnect (src/lib/offline/sync.ts) or manually from the queue screen.
 * Each item is independent: one failure does not stop the rest from
 * uploading. `uploader` is injectable so tests can prove the upload/submit
 * sequence without a network.
 */
export async function processQueue(uploader: CaptureUploader = defaultUploader): Promise<ProcessQueueResult> {
  const db = await getOfflineDb();
  const pending = await listByStatus('pending');
  const result: ProcessQueueResult = { uploaded: [], failed: [], stillPending: [] };

  for (const item of pending) {
    if (!item.captureJobId) {
      // A row from an earlier build. Stopped at once rather than retried,
      // because the answer will be the same 400 every time, and it lands in
      // the flagged list where the operator can discard it.
      item.status = 'failed';
      item.lastError = NO_JOB_ERROR;
      item.updatedAt = new Date().toISOString();
      await db.put('queue', item);
      result.failed.push(item.localId);
      continue;
    }

    item.status = 'uploading';
    item.updatedAt = new Date().toISOString();
    await db.put('queue', item);

    try {
      // Each frame presigns and uploads on its own fingerprint, so a frame
      // the tenant already holds skips its bytes while the frames beside it
      // still go up. They run in order: the first asset key is the one the
      // review console shows as the capture's thumbnail.
      const assetKeys: string[] = [];
      for (const asset of item.assets) {
        const { assetKey } = await uploader.uploadCaptureAsset({
          mode: item.mode,
          fingerprint: asset.fingerprint,
          bytes: new Blob([asset.bytes], { type: asset.contentType }),
          contentType: asset.contentType,
          captureJobId: item.captureJobId,
        });
        assetKeys.push(assetKey);
      }

      const response = await uploader.createCapture(item.captureJobId, {
        mode: item.mode,
        workflow: item.workflow,
        fingerprint: item.fingerprint,
        assetKeys,
        sessionId: item.sessionId,
        workItemId: item.workItemId,
        siteId: item.siteId,
        teamId: item.teamId,
        voiceTranscript: item.voiceTranscript,
        clientQuality: item.clientQuality,
        manualFields: item.manualFields,
      });

      item.status = 'uploaded';
      item.captureId = response.captureId;
      item.updatedAt = new Date().toISOString();
      await db.put('queue', item);
      result.uploaded.push(item.localId);
    } catch (error) {
      item.attempts += 1;
      item.lastError = error instanceof Error ? error.message : 'Unknown upload error';
      item.updatedAt = new Date().toISOString();
      if (error instanceof ApiError && error.isPaymentRequired) {
        // No credits: every retry gets the same 402. Stop now and keep the
        // API's message where the operator reads it, with discard offered.
        item.status = 'failed';
        item.refusal = 'payment_required';
        result.failed.push(item.localId);
      } else if (error instanceof ApiError && error.isDuplicateInJob) {
        // The job already holds this photo. Every retry gets the same 409.
        item.status = 'failed';
        item.refusal = 'duplicate_in_job';
        result.failed.push(item.localId);
      } else if (item.attempts >= MAX_AUTO_RETRY_ATTEMPTS) {
        item.status = 'failed';
        result.failed.push(item.localId);
      } else {
        item.status = 'pending';
        result.stillPending.push(item.localId);
      }
      await db.put('queue', item);
    }
  }

  return result;
}
